import {
    Box,
    VStack,
    Flex,
    HStack,
    Text,
    Alert,
    AlertIcon,
    Table,
    Thead,
    Tbody,
    Tr,
    Td,
    Badge,
    Button,
    Icon,
    Spinner,
} from '@chakra-ui/react';
import { FiCheck, FiList, FiClock } from 'react-icons/fi';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExportsFeedPaginated } from './hooks/useExportsFeed';
import { SegmentedControl } from '../../ui';
import ExportsByRoLedger from './components/ExportsByRoLedger';
import RefreshButton from '../../components/RefreshButton';
import LocationSelector from '../../components/LocationSelector';
import TableSettingsButton from '../../components/TableSettingsButton';
import { TbBuilding } from 'react-icons/tb';
import EMSDetailModal from '../../modals/EMSDetailModal';
import { useFingerprint } from '../../hooks/useFingerprint';
import Pagination from '../../components/Pagination';
import SortArrowIcon from '../../components/SortArrowIcon';
import { Vin } from '../../components/orders/components';
import CollapsibleSearch, { LIST_PAGE_SEARCH_WIDTH } from '../../components/CollapsibleSearch';
import { useTableSettings } from '../../hooks/useTableSettings';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { useScrollShadow } from '../../hooks/useScrollShadow';
import { useStickyTableCopyFront } from '../../hooks/useStickyTableCopyFront';
import { stickyTableScrollCopySx, STICKY_TABLE_HEADER_Z_INDEX } from '../../utils/tableCopySx';
import ResizableTh from '../../components/ResizableTh';
import { useResponsiveToolbar } from '../../hooks/useResponsiveToolbar';
import usePageTitle from '../../hooks/usePageTitle';
import ListTabRow from '../../components/ListTabRow';

type FilterType = 'all' | 'pending' | 'submitted';

type ActivitySortField = 'numParts' | 'received' | null;

interface ActivitySortState {
    field: ActivitySortField;
    direction: 'asc' | 'desc';
}

const defaultSortState: ActivitySortState = { field: null, direction: 'asc' };

const toggleActivitySort = (
    currentState: ActivitySortState,
    field: ActivitySortField
): ActivitySortState => {
    if (currentState.field !== field) {
        return { field, direction: 'asc' };
    }

    if (currentState.direction === 'asc') {
        return { field, direction: 'desc' };
    }

    return { field: null, direction: 'asc' };
};

const Exports = () => {
    useFingerprint();
    const { settings: tableSettings, updateSettings: updateTableSettings, tableSettingsSx } = useTableSettings('activity');
    const { widths: colWidths, updateWidth: updateColWidth, resetWidths: resetColWidths } = useColumnWidths('activity');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { showRightShadow } = useScrollShadow(scrollContainerRef);
    const { containerRef: toolbarRef, locationCompact, searchCompact } = useResponsiveToolbar();
    usePageTitle('Exports');

    const tabIcons: Record<string, React.ElementType> = {
        'all': FiList as React.ElementType,
        'pending': FiClock as React.ElementType,
        'submitted': FiCheck as React.ElementType,
    };

    /**
     * Feed = flat live feed of EMS receipts (default). By RO = master–detail:
     * RO list on the left, that RO's full export ledger on the right. Both
     * views share the status pills, search, location filter, and pagination.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const view: 'feed' | 'ro' = searchParams.get('view') === 'ro' ? 'ro' : 'feed';
    const handleViewChange = useCallback((next: 'feed' | 'ro') => {
        setSearchParams(
            (previous) => {
                const params = new URLSearchParams(previous);
                if (next === 'ro') {
                    params.set('view', 'ro');
                } else {
                    params.delete('view');
                    params.delete('ro');
                }
                return params;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const [activeFilter, setActiveFilter] = useState<FilterType>('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [selectedLocationIds, setSelectedLocationIds] = useState<Array<string> | 'all'>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortState, setSortState] = useState<ActivitySortState>(defaultSortState);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1); // Reset to first page when search changes
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // We intentionally fetch one extra record to reliably detect whether a "next" page exists.
    // Without a totalCount/hasMore from the API, `orders.length === limit` is ambiguous.
    const limit = 20;
    const queryLimit = limit + 1;

    const {
        data: ordersFeedData,
        isLoading: isOrdersFeedLoading,
        isRefetching,
        error: ordersFeedError,
        refetch: refetchOrders
    } = useExportsFeedPaginated(activeFilter, selectedLocationIds, currentPage, queryLimit, debouncedSearch);

    const handleRefetch = useCallback(() => {
        refetchOrders();
    }, [refetchOrders]);

    const handleFilterChange = useCallback((filter: FilterType) => {
        setActiveFilter(filter);
        setCurrentPage(1);
        setSortState(defaultSortState);
        setSearchQuery('');
        setDebouncedSearch('');
    }, []);

    const handleLocationChange = useCallback((newLocationIds: Array<string> | 'all') => {
        setSelectedLocationIds(newLocationIds);
        setCurrentPage(1);
    }, []);

    const handleSortChange = useCallback((field: ActivitySortField) => {
        setSortState(prev => toggleActivitySort(prev, field));
    }, []);

    const handleOrderClick = (orderId: string) => {
        setSelectedOrderId(orderId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedOrderId(null);
    };

    const { orders, hasNextPage } = useMemo(() => {
        const fetched = ordersFeedData?.orders || [];
        return {
            orders: fetched.slice(0, limit),
            hasNextPage: fetched.length > limit,
        };
    }, [ordersFeedData, limit]);

    const sortedOrders = useMemo(() => {
        if (!sortState.field) return orders;

        return [...orders].sort((a, b) => {
            let aValue: number;
            let bValue: number;

            if (sortState.field === 'numParts') {
                aValue = a.numParts;
                bValue = b.numParts;
            } else if (sortState.field === 'received') {
                aValue = new Date(a.received).getTime();
                bValue = new Date(b.received).getTime();
            } else {
                return 0;
            }

            if (sortState.direction === 'asc') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });
    }, [orders, sortState]);

    const { scrollContainerProps: stickyCopyScrollProps } = useStickyTableCopyFront(
        scrollContainerRef,
        `${isOrdersFeedLoading}-${sortedOrders.length}-${activeFilter}`,
    );

    const totalPages = useMemo(() => {
        return hasNextPage ? currentPage + 1 : currentPage;
    }, [hasNextPage, currentPage]);

    return (
        <Box
            w="calc(100% + 2rem)"
            mx={-4}
            mt={-4}
            mb={-2}
            h="calc(100dvh - var(--partbay-topbar))"
            display="flex"
            flexDirection="column"
            pb={0}
            overflow="hidden"
        >
            {/* Row 1 — status tabs + toolbar actions, hairline underneath (ENG-2297). */}
            <ListTabRow
                actions={
                    <HStack spacing={2} flexShrink={0}>
                        <SegmentedControl
                            size="sm"
                            value={view}
                            onChange={handleViewChange}
                            items={[
                                { value: 'feed', label: 'Feed' },
                                { value: 'ro', label: 'By RO' },
                            ]}
                        />
                        {view === 'feed' && (
                            <TableSettingsButton
                                settings={tableSettings}
                                onUpdateSettings={updateTableSettings}
                                hasCustomColumnWidths={Object.keys(colWidths).length > 0}
                                onResetColumnWidths={resetColWidths}
                            />
                        )}
                        <RefreshButton
                            refetch={handleRefetch}
                            isLoading={isRefetching}
                        />
                    </HStack>
                }
            >
                    {['all', 'pending', 'submitted'].map(el => {
                        const isActive = activeFilter === el;
                        return (
                            <Button
                                key={el}
                                size="sm"
                                h="32px"
                                px={4}
                                borderRadius="full"
                                fontWeight="medium"
                                fontSize="sm"
                                flexShrink={0}
                                bg={isActive ? 'brand.solid' : 'transparent'}
                                color={isActive ? 'white' : 'gray.600'}
                                _hover={{ bg: isActive ? 'brand.solid' : 'gray.100' }}
                                _active={{ bg: isActive ? 'brand.solid' : 'gray.100' }}
                                onClick={() => handleFilterChange(el as FilterType)}
                                leftIcon={<Icon as={tabIcons[el]} boxSize={3.5} />}
                            >
                                {el[0].toUpperCase() + el.slice(1)}
                            </Button>
                        );
                    })}
            </ListTabRow>

            {/* Row 2 — search left, location far right (ENG-2297 #4). */}
            <Flex
                ref={toolbarRef}
                my={3}
                px={6}
                flexShrink={0}
                align="center"
                gap={2}
            >
                <CollapsibleSearch
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by VIN or RO#..."
                    isCompact={searchCompact}
                    width={LIST_PAGE_SEARCH_WIDTH}
                />
                <Box ml="auto" flexShrink={0}>
                    <LocationSelector
                        storageKey="location_ids"
                        onLocationChange={handleLocationChange}
                        width="260px"
                        icon={TbBuilding}
                        iconOnly={locationCompact}
                    />
                </Box>
            </Flex>
            {ordersFeedData?.desktopHeartbeatInvalid && (
                <Box px={6} mb={4} flexShrink={0}>
                    <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="sm">
                            We have not verified a healthy status of the desktop app from{' '}
                            <Text as="span" fontWeight="semibold">
                                {ordersFeedData.lateHeartbeatLocations.length > 0
                                    ? ordersFeedData.lateHeartbeatLocations.join(', ')
                                    : 'the affected location(s)'}
                            </Text>{' '}
                            since{' '}
                            {ordersFeedData.latestDesktopHeartbeat
                                ? new Date(ordersFeedData.latestDesktopHeartbeat).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                })
                                : 'an unknown time'}
                            . EMS may not be up to date. Please contact IT if this issue continues.
                        </Text>
                    </Alert>
                </Box>
            )}

            {/* Table section - fills remaining space */}
            <Box
                flex={1}
                display="flex"
                flexDirection="column"
                overflow="hidden"
                minH={0}
            >
                {isOrdersFeedLoading && !ordersFeedData ? (
                    <Flex justify="center" align="center" h="400px">
                        <Spinner size="xl" color="blue.500" />
                    </Flex>
                ) : ordersFeedError ? (
                    <Alert status="error">
                        <AlertIcon />
                        Failed to load orders feed. Please try again.
                    </Alert>
                ) : view === 'ro' ? (
                    <ExportsByRoLedger
                        orders={sortedOrders}
                        searchQuery={debouncedSearch}
                        footer={
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                isLoading={isOrdersFeedLoading || isRefetching}
                                hasNextPage={hasNextPage}
                            />
                        }
                    />
                ) : sortedOrders.length > 0 ? (
                    <>
                        <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
                        <Box
                            ref={scrollContainerRef}
                            {...stickyCopyScrollProps}
                            flex={1}
                            minH={0}
                            overflow="auto"
                            borderTop="1px solid"
                            borderColor="gray.200"
                            sx={{
                                ...tableSettingsSx,
                                ...stickyTableScrollCopySx,
                                overscrollBehavior: 'contain',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 transparent',
                                '& table': {
                                    minWidth: '100%',
                                },
                                // Bottom border on sticky header + solid background so content doesn't bleed through
                                '& th': {
                                    ...(tableSettingsSx['& th'] || {}),
                                    borderBottomWidth: '0 !important',
                                    boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                                    bg: 'gray.50',
                                },
                            }}
                        >
                            <Table variant="simple" size="sm" w="100%" sx={Object.keys(colWidths).length > 0 ? { tableLayout: 'fixed', '& td': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } : undefined}>
                                <Thead position="sticky" top={0} zIndex={STICKY_TABLE_HEADER_Z_INDEX}>
                                    <Tr bg="gray.50">
                                        <ResizableTh columnKey="vehicle" storedWidth={colWidths.vehicle} onResize={updateColWidth} minWidth={120} py={2.5} pl={8} pr={3} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Vehicle</ResizableTh>
                                        <ResizableTh columnKey="location" storedWidth={colWidths.location} onResize={updateColWidth} minWidth={100} py={2.5} px={3} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Location</ResizableTh>
                                        <ResizableTh columnKey="insurance" storedWidth={colWidths.insurance} onResize={updateColWidth} minWidth={100} py={2.5} px={3} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Insurance</ResizableTh>
                                        <ResizableTh columnKey="received" storedWidth={colWidths.received} onResize={updateColWidth} minWidth={100} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200">
                                            <Flex
                                                as="button"
                                                type="button"
                                                align="center"
                                                justify="center"
                                                gap="6px"
                                                w="100%"
                                                color={sortState.field === 'received' ? "brand.hover" : "gray.500"}
                                                fontSize="11px"
                                                fontWeight="semibold"
                                                textTransform="uppercase"
                                                letterSpacing="wider"
                                                cursor="pointer"
                                                onClick={() => handleSortChange('received')}
                                                _hover={{ color: "brand.hover" }}
                                                transition="color 0.2s"
                                            >
                                                <Text as="span">Received</Text>
                                                <Box lineHeight={0} color={sortState.field === 'received' ? "brand.hover" : "gray.400"}>
                                                    <SortArrowIcon direction={sortState.field === 'received' ? (sortState.direction === 'asc' ? 'asc' : 'desc') : 'none'} size={12} />
                                                </Box>
                                            </Flex>
                                        </ResizableTh>
                                        <ResizableTh columnKey="parts" storedWidth={colWidths.parts} onResize={updateColWidth} minWidth={70} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200">
                                            <Flex
                                                as="button"
                                                type="button"
                                                align="center"
                                                justify="center"
                                                gap="6px"
                                                w="100%"
                                                color={sortState.field === 'numParts' ? "brand.hover" : "gray.500"}
                                                fontSize="11px"
                                                fontWeight="semibold"
                                                textTransform="uppercase"
                                                letterSpacing="wider"
                                                cursor="pointer"
                                                onClick={() => handleSortChange('numParts')}
                                                _hover={{ color: "brand.hover" }}
                                                transition="color 0.2s"
                                            >
                                                <Text as="span">Parts</Text>
                                                <Box lineHeight={0} color={sortState.field === 'numParts' ? "brand.hover" : "gray.400"}>
                                                    <SortArrowIcon direction={sortState.field === 'numParts' ? (sortState.direction === 'asc' ? 'asc' : 'desc') : 'none'} size={12} />
                                                </Box>
                                            </Flex>
                                        </ResizableTh>
                                        <ResizableTh columnKey="status" storedWidth={colWidths.status} onResize={updateColWidth} minWidth={100} py={2.5} px={3} textAlign="center" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Status</ResizableTh>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {sortedOrders.map((order, rowIndex) => (
                                        <Tr
                                            key={order._id}
                                            cursor='pointer'
                                            _hover={{ bg: 'gray.50' }}
                                            onClick={() => handleOrderClick(order._id)}
                                        >
                                            <Td py={3} pl={8} pr={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <VStack align="flex-start" spacing={0}>
                                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                                        {order.title}
                                                    </Text>
                                                    <Vin vin={order.vin} />
                                                </VStack>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Text fontSize="sm" color="gray.700">
                                                    {order.location}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Text fontSize="sm" color="gray.700">
                                                    {order.insurance ?? '—'}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} textAlign="center" borderBottomWidth="1px" borderColor="gray.100">
                                                <Text fontSize="sm" color="gray.700">
                                                    {(() => {
                                                        const orderDate = new Date(order.received);
                                                        const today = new Date();

                                                        const isToday = orderDate.toDateString() === today.toDateString();

                                                        if (isToday) {
                                                            return orderDate.toLocaleTimeString('en-US', {
                                                                hour: 'numeric',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            }).toLowerCase();
                                                        } else {
                                                            return orderDate.toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            });
                                                        }
                                                    })()}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} textAlign="center" borderBottomWidth="1px" borderColor="gray.100">
                                                <Text fontSize="sm" color="gray.700" textAlign="center">
                                                    {order.numParts}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} textAlign="center" borderBottomWidth="1px" borderColor="gray.100">
                                                <Badge
                                                    bg={order.status === 'pending' ? 'yellow.500' : 'blue.500'}
                                                    color="white"
                                                    size="sm"
                                                    w="112px"
                                                    py={1}
                                                    fontSize="xs"
                                                    fontWeight="semibold"
                                                    borderRadius="lg"
                                                    display="inline-flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    gap={1}
                                                >
                                                    <Icon
                                                        as={(order.status === 'pending' ? FaExclamationTriangle : FiCheck) as React.ElementType}
                                                        w={3}
                                                        h={3}
                                                    />
                                                    {order.status}
                                                </Badge>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                        {showRightShadow && (
                            <Box
                                position="absolute"
                                right={0}
                                top={0}
                                bottom={0}
                                w="48px"
                                bgGradient="linear(to-r, rgba(255,255,255,0), rgba(255,255,255,0.85))"
                                pointerEvents="none"
                                zIndex={4}
                            />
                        )}
                        </Box>
                        <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                isLoading={isOrdersFeedLoading || isRefetching}
                                hasNextPage={hasNextPage}
                            />
                        </Box>
                    </>
                ) : (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                        borderTop="1px solid"
                        borderColor="gray.200"
                    >
                        <VStack spacing={2}>
                            <Text fontSize="md" color="gray.500">
                                {searchQuery ? 'No matching orders found' : 'No orders found'}
                            </Text>
                            <Text fontSize="sm" color="gray.400">
                                {searchQuery ? 'Try a different search term' : 'Orders will appear here once you upload EMS files'}
                            </Text>
                        </VStack>
                    </Box>
                )}
            </Box>

            {selectedOrderId && (
                <EMSDetailModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    emsId={selectedOrderId}
                />
            )}
        </Box>
    );
};

export default Exports;